import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  MenuItem,
  Tabs,
  Tab,
  Link,
  Tooltip,
} from '@mui/material';
import {
  PersonAdd,
  Close,
  Person,
  Email,
  Phone,
  OpenInFull,
  HelpOutline,
} from '@mui/icons-material';
import { customerAPI } from '../../services/api';
import { toast } from 'react-toastify';

const countryOptions = ['India', 'United Kingdom (UK)'];
const cityOptions = ['Gurugram', 'Mumbai', 'Bangalore'];

/**
 * CustomerSelect - Reusable customer autocomplete with "Add new" functionality
 * 
 * Props:
 * - value: customer object
 * - onChange: callback with selected customer object
 * - label: optional label text
 * - customers: optional pre-loaded customers array
 * - onCustomersChange: callback when customers list changes
 */
const CustomerSelect = ({ 
  value, 
  onChange, 
  label = 'Customer',
  customers: externalCustomers,
  onCustomersChange,
}) => {
  const [customers, setCustomers] = useState(externalCustomers || []);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customerTab, setCustomerTab] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    type: 'person',
    name: '',
    email: '',
    phone: '',
    company: '',
    street: '',
    city: '',
    zip: '',
    state: '',
    country: 'India',
    gstin: '',
  });

  useEffect(() => {
    if (!externalCustomers) {
      fetchCustomers();
    }
  }, [externalCustomers]);

  useEffect(() => {
    if (externalCustomers) {
      setCustomers(externalCustomers);
    }
  }, [externalCustomers]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customerAPI.getAllForDropdown();
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    if (!newCustomer.email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      const response = await customerAPI.create({
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone || '',
        company: newCustomer.company || '',
        customerType: newCustomer.type === 'company' ? 'business' : 'individual',
        gstNumber: newCustomer.gstin || '',
        address: {
          street: newCustomer.street || '',
          city: newCustomer.city || '',
          state: newCustomer.state || '',
          zipCode: newCustomer.zip || '',
          country: newCustomer.country || 'India',
        },
      });
      
      const createdCustomer = response.data.customer;
      const updatedCustomers = [...customers, createdCustomer];
      setCustomers(updatedCustomers);
      
      if (onCustomersChange) {
        onCustomersChange(updatedCustomers);
      }
      
      onChange(createdCustomer);
      handleCloseDialog();
      toast.success('Customer created successfully');
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error(error.response?.data?.message || 'Failed to create customer');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCustomerTab(0);
    setNewCustomer({
      type: 'person',
      name: '',
      email: '',
      phone: '',
      company: '',
      street: '',
      city: '',
      zip: '',
      state: '',
      country: 'India',
      gstin: '',
    });
  };

  // Pre-compute filtered options based on inputValue
  // This ensures the options array reference changes on every search,
  // forcing MUI Autocomplete to fully re-render the listbox (no stale items)
  const filteredOptions = useMemo(() => {
    const searchTerm = (inputValue || '').toLowerCase().trim();
    
    const addNewOption = {
      isAddNew: true,
      label: searchTerm
        ? `+ Create "${inputValue}"`
        : `+ Create "New Customer"`,
      inputValue: inputValue,
    };
    
    if (!searchTerm) {
      return [addNewOption];
    }
    
    const prefixMatches = [];
    const containsMatches = [];
    
    customers.forEach(option => {
      const companyName = String(option.company || option.name || '').toLowerCase();
      if (!companyName) return;
      
      if (companyName.startsWith(searchTerm)) {
        prefixMatches.push(option);
      } else if (companyName.includes(searchTerm)) {
        containsMatches.push(option);
      }
    });
    
    return [...prefixMatches, ...containsMatches, addNewOption];
  }, [customers, inputValue]);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
        <Typography sx={{ width: { xs: 'auto', sm: 140 }, minWidth: { sm: 140 }, color: '#666', fontSize: '14px', fontWeight: 500, mb: { xs: 0.5, sm: 0 }, pt: 1 }}>
          {label}
        </Typography>
        <Autocomplete
          size="small"
          options={filteredOptions}
          value={value}
          inputValue={inputValue}
          open={open && inputValue.length > 0}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onInputChange={(event, newInputValue, reason) => {
            setInputValue(newInputValue);
            // Open dropdown when user types, close when empty
            if (reason === 'input') {
              setOpen(newInputValue.length > 0);
            }
            // Clear selection when user starts typing a new search
            if (reason === 'input' && value && newInputValue) {
              onChange(null);
            }
          }}
          onChange={(e, newValue) => {
            if (newValue && newValue.isAddNew) {
              setDialogOpen(true);
            } else {
              onChange(newValue);
              setInputValue('');
            }
          }}
          loading={loading}
          getOptionLabel={(option) => {
            if (option.isAddNew) return option.label;
            return option?.company || option?.name || '';
          }}
          getOptionKey={(option) => {
            if (option.isAddNew) return `add-new-${option.inputValue || 'empty'}`;
            return option._id || option.id || option.company || option.name;
          }}
          isOptionEqualToValue={(option, val) => {
            if (!option || !val) return false;
            if (option.isAddNew || val.isAddNew) return false;
            return option._id === val._id;
          }}
          filterOptions={(opts) => opts}
          noOptionsText="No customers found"
          renderOption={(props, option) => {
            // CRITICAL: Extract key from props (React 18 requirement)
            // Otherwise React can't properly reconcile DOM and stale items persist
            const { key, ...restProps } = props;
            if (option.isAddNew) {
              return (
                <Box
                  key={key}
                  component="li"
                  {...restProps}
                  sx={{ 
                    color: '#875A7B', 
                    fontWeight: 500,
                    '&:hover': { backgroundColor: 'rgba(135, 90, 123, 0.08)' }
                  }}
                >
                  <PersonAdd sx={{ mr: 1, fontSize: 18 }} />
                  {option.label}
                </Box>
              );
            }
            return (
              <Box key={key} component="li" {...restProps} sx={{ whiteSpace: 'normal', overflow: 'visible' }}>
                {option.company || option.name}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder="Type company name to search..."
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
              sx={{ borderBottom: '1px solid #dee2e6' }}
            />
          )}
          slotProps={{
            paper: {
              sx: {
                maxHeight: '300px',
                '& .MuiAutocomplete-option': {
                  whiteSpace: 'normal',
                  height: 'auto',
                  padding: '8px 12px',
                },
              },
            },
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      {/* Add Customer Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            minHeight: '80vh',
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          py: 1.5,
          px: 2,
          backgroundColor: '#f8f8f8',
        }}>
          <Typography sx={{ fontWeight: 500, fontSize: '16px', color: '#333' }}>
            Create Customer
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" sx={{ color: '#666' }}>
              <OpenInFull sx={{ fontSize: '18px' }} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleCloseDialog}
              sx={{ color: '#666' }}
            >
              <Close sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {/* Main Form Area */}
          <Box sx={{ p: 3, flex: 1 }}>
            {/* Top Section: Avatar + Type + Name + Contact */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              {/* Avatar */}
              <Box sx={{ 
                width: 100, 
                height: 100, 
                backgroundColor: '#f5f0f0', 
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Person sx={{ fontSize: 60, color: '#d4a5a5' }} />
              </Box>
              
              {/* Name & Contact */}
              <Box sx={{ flex: 1 }}>
                {/* Person/Company Toggle */}
                <RadioGroup
                  row
                  value={newCustomer.type}
                  onChange={(e) => setNewCustomer({ ...newCustomer, type: e.target.value })}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel 
                    value="person" 
                    control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                    label={<Typography sx={{ fontSize: '13px' }}>Person</Typography>}
                  />
                  <FormControlLabel 
                    value="company" 
                    control={<Radio size="small" sx={{ color: '#00a09d', '&.Mui-checked': { color: '#00a09d' } }} />} 
                    label={<Typography sx={{ fontSize: '13px' }}>Company</Typography>}
                  />
                </RadioGroup>
                
                {/* Name Field */}
                <TextField
                  fullWidth
                  variant="standard"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  placeholder="e.g. Brandon Freeman"
                  InputProps={{
                    disableUnderline: true,
                    sx: { 
                      fontSize: '28px', 
                      fontWeight: 400,
                      color: '#333',
                      '&::placeholder': { color: '#999', opacity: 1 }
                    }
                  }}
                  sx={{ mb: 1 }}
                />
                
                {/* Email & Phone */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                    <Email sx={{ fontSize: '16px', color: '#666' }} />
                    <TextField
                      fullWidth
                      variant="standard"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                      placeholder="Email"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px', color: '#666' }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                    <Phone sx={{ fontSize: '16px', color: '#666' }} />
                    <TextField
                      fullWidth
                      variant="standard"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                      placeholder="Phone"
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '14px', color: '#666' }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Two Column Layout */}
            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                {/* Company */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography sx={{ width: '100px', fontSize: '13px', color: '#333', pt: 1 }}>Company</Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={newCustomer.company}
                    onChange={(e) => setNewCustomer({ ...newCustomer, company: e.target.value })}
                    placeholder="Company Name..."
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                    }}
                  />
                </Box>
                
                {/* Address */}
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <Typography sx={{ width: '100px', fontSize: '13px', color: '#333', pt: 1 }}>Address</Typography>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      variant="standard"
                      value={newCustomer.street}
                      onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
                      placeholder="Street..."
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5, mb: 1 }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <TextField
                        select
                        variant="standard"
                        value={newCustomer.city}
                        onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                        sx={{ flex: 1 }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                      >
                        <MenuItem value="">Select City</MenuItem>
                        {cityOptions.map((city) => (
                          <MenuItem key={city} value={city}>{city}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        variant="standard"
                        value={newCustomer.zip}
                        onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
                        placeholder="ZIP"
                        sx={{ width: '80px' }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                      />
                      <TextField
                        variant="standard"
                        value={newCustomer.state}
                        onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value })}
                        placeholder="State"
                        sx={{ flex: 1 }}
                        InputProps={{
                          disableUnderline: true,
                          sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                        }}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      select
                      variant="standard"
                      value={newCustomer.country}
                      onChange={(e) => setNewCustomer({ ...newCustomer, country: e.target.value })}
                      InputProps={{
                        disableUnderline: true,
                        sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                      }}
                    >
                      {countryOptions.map((country) => (
                        <MenuItem key={country} value={country}>{country}</MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Box>
              </Grid>
              
              {/* Right Column */}
              <Grid item xs={12} md={6}>
                {/* GSTIN */}
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Box sx={{ width: '120px', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={{ fontSize: '13px', color: '#333' }}>GSTIN</Typography>
                    <Tooltip title="Goods and Services Tax Identification Number">
                      <HelpOutline sx={{ fontSize: '14px', color: '#00a09d', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={newCustomer.gstin}
                    onChange={(e) => setNewCustomer({ ...newCustomer, gstin: e.target.value })}
                    placeholder="not applicable"
                    InputProps={{
                      disableUnderline: true,
                      sx: { fontSize: '13px', borderBottom: '1px solid #e0e0e0', pb: 0.5 }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Tabs Section */}
          <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
            <Tabs
              value={customerTab}
              onChange={(e, newValue) => setCustomerTab(newValue)}
              sx={{
                minHeight: '40px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '13px',
                  minHeight: '40px',
                  py: 1,
                  px: 2,
                  color: '#666',
                  '&.Mui-selected': { color: '#333', fontWeight: 500 },
                },
                '& .MuiTabs-indicator': { backgroundColor: '#333' },
              }}
            >
              <Tab label="Contacts" />
              <Tab label="Sales & Purchase" />
              <Tab label="Invoicing" />
              <Tab label="Notes" />
            </Tabs>
            
            {/* Tab Content */}
            <Box sx={{ p: 2, minHeight: '100px', backgroundColor: '#fafafa' }}>
              {customerTab === 0 && (
                <Box sx={{ 
                  border: '1px dashed #ccc', 
                  borderRadius: '4px', 
                  p: 2, 
                  textAlign: 'center',
                  backgroundColor: '#fff',
                }}>
                  <Link
                    component="button"
                    sx={{ 
                      color: '#00a09d', 
                      textDecoration: 'none', 
                      fontSize: '13px',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Add Contact
                  </Link>
                </Box>
              )}
              {customerTab === 1 && (
                <Typography sx={{ fontSize: '13px', color: '#666' }}>
                  Sales & Purchase settings will be available after creating the customer.
                </Typography>
              )}
              {customerTab === 2 && (
                <Typography sx={{ fontSize: '13px', color: '#666' }}>
                  Invoicing settings will be available after creating the customer.
                </Typography>
              )}
              {customerTab === 3 && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Internal notes..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '13px',
                      backgroundColor: '#fff',
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        
        {/* Footer */}
        <DialogActions sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f8f8',
          justifyContent: 'flex-start',
        }}>
          <Button
            variant="contained"
            onClick={handleAddCustomer}
            sx={{
              backgroundColor: '#00a09d',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008f8c' },
            }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            sx={{
              backgroundColor: '#4a4a4a',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#333' },
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerSelect;
